/**
 * /bezahlung — Zahlungsstand einer freigegebenen Buchung (Token aus der Bezahl-Mail).
 *
 * Hierher kommen: Rückkehr aus dem Stripe-Checkout (`ergebnis=danke` / `abgebrochen`),
 * der Bezahllink, wenn er nicht zu Stripe weiterleitet (Frist abgelaufen, schon bezahlt,
 * storniert, Stripe-Fehler `ergebnis=fehler`, kaputter Link `ergebnis=ungueltig`).
 *
 * Die Seite bestätigt nie selbst. Nach `ergebnis=danke` fragt sie den Stand ab, bis der
 * Stripe-Webhook die Buchung bestätigt hat (meist wenige Sekunden).
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarCheck,
  CalendarPlus,
  CheckCircle2,
  Clock,
  CreditCard,
  Loader2,
  Mail,
  XCircle,
  type LucideIcon,
} from "lucide-react";

import { ApiError, bookingsApi } from "@/lib/api";
import {
  CONFIRM_POLL_MS,
  CONFIRM_WAIT_MS,
  googleCalendarUrl,
  resolvePaymentView,
  type PaymentViewKind,
} from "@/lib/paymentView";
import { formatDate, formatPriceEur, formatRange } from "@/lib/format";
import type { PaymentInfo } from "@/types/api";

interface ViewText {
  icon: LucideIcon;
  tone: "success" | "info" | "warning" | "danger";
  title: string;
  text: (info: PaymentInfo | undefined) => string;
}

const due = (info: PaymentInfo | undefined) =>
  info?.payment_due_at
    ? `${formatDate(info.payment_due_at, "EEEE, dd.MM.yyyy, HH:mm")} Uhr`
    : "";

const VIEWS: Record<Exclude<PaymentViewKind, "loading">, ViewText> = {
  confirmed: {
    icon: CheckCircle2,
    tone: "success",
    title: "Zahlung bestätigt",
    text: (info) =>
      info?.paid_at
        ? "Danke! Deine Zahlung ist eingegangen, die Buchung ist damit fest."
        : "Deine Buchung ist bestätigt.",
  },
  waiting: {
    icon: Loader2,
    tone: "info",
    title: "Zahlung wird bestätigt",
    text: () =>
      "Danke für deine Zahlung! Wir warten noch auf die Bestätigung von Stripe. Das dauert meist nur wenige Sekunden, diese Seite aktualisiert sich von selbst.",
  },
  waiting_long: {
    icon: Clock,
    tone: "info",
    title: "Zahlung in Bearbeitung",
    text: () =>
      "Die Bestätigung dauert länger als üblich, zum Beispiel bei manchen Zahlungsarten. Du musst nichts weiter tun: Sobald die Zahlung bestätigt ist, bekommst du die Buchungsbestätigung per Email. Bitte nicht noch einmal bezahlen.",
  },
  open: {
    icon: CreditCard,
    tone: "warning",
    title: "Zahlung offen",
    text: (info) =>
      `Charlie hat deine Buchung freigegeben. Bitte bezahle bis ${due(info)}, damit sie fest wird. Danach wird sie automatisch storniert.`,
  },
  aborted: {
    icon: CreditCard,
    tone: "warning",
    title: "Zahlung abgebrochen",
    text: (info) =>
      `Es wurde nichts abgebucht. Du kannst bis ${due(info)} bezahlen, danach wird die Buchung automatisch storniert.`,
  },
  stripe_error: {
    icon: AlertTriangle,
    tone: "danger",
    title: "Zahlung gerade nicht möglich",
    text: (info) =>
      `Der Zahlungsdienst ist gerade nicht erreichbar. Bitte versuch es in ein paar Minuten noch einmal, die Frist läuft bis ${due(info)}.`,
  },
  deadline_passed: {
    icon: Clock,
    tone: "danger",
    title: "Zahlfrist abgelaufen",
    text: () =>
      "Die Zahlfrist für diese Buchung ist abgelaufen. Die Buchung wird automatisch storniert und der Raum wieder freigegeben.",
  },
  expired: {
    icon: XCircle,
    tone: "danger",
    title: "Buchung storniert",
    text: () =>
      "Bis zum Ende der Zahlfrist ist keine Zahlung eingegangen, deshalb wurde die Buchung storniert. Wenn der Raum noch frei ist, kannst du gern eine neue Anfrage stellen.",
  },
  cancelled: {
    icon: XCircle,
    tone: "danger",
    title: "Buchung storniert",
    text: () =>
      "Diese Buchung wurde storniert. Eine Zahlung ist nicht mehr möglich.",
  },
  rejected: {
    icon: XCircle,
    tone: "danger",
    title: "Anfrage abgelehnt",
    text: () => "Diese Anfrage wurde abgelehnt. Eine Zahlung ist nicht nötig.",
  },
  not_approved: {
    icon: Clock,
    tone: "info",
    title: "Noch nicht freigegeben",
    text: () =>
      "Charlie hat diese Anfrage noch nicht freigegeben. Sobald das passiert, bekommst du eine Email mit dem Bezahllink.",
  },
  invalid: {
    icon: AlertTriangle,
    tone: "danger",
    title: "Link ungültig",
    text: () =>
      "Dieser Link ist ungültig oder abgelaufen. Bitte öffne den Link aus deiner letzten Email von Tanzraum oder schau unter „Meine Buchungen“ nach.",
  },
  network_error: {
    icon: AlertTriangle,
    tone: "danger",
    title: "Verbindung fehlgeschlagen",
    text: () =>
      "Der Zahlungsstand konnte gerade nicht geladen werden. Bitte lade die Seite neu.",
  },
};

const TONE_CLASSES: Record<ViewText["tone"], string> = {
  success: "border-success/40 bg-success/10 text-success",
  info: "border-info/40 bg-info/10 text-info",
  warning: "border-warning/50 bg-warning/10 text-ink",
  danger: "border-danger/40 bg-danger/10 text-danger",
};

const PRIMARY_LINK =
  "inline-flex items-center justify-center gap-2 rounded-card bg-primary px-6 py-3 text-lead font-medium text-white shadow-card transition-all duration-200 hover:bg-primary-dark hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

const SECONDARY_LINK =
  "inline-flex items-center justify-center gap-2 rounded-card border border-line bg-surface px-6 py-3 text-lead font-medium text-ink transition-all duration-200 hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

function BookingSummary({ info }: { info: PaymentInfo }) {
  return (
    <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 rounded-card border border-line bg-surface p-5 text-left text-body-sm shadow-card">
      <dt className="font-semibold text-ink">Raum</dt>
      <dd className="text-ink-soft">{info.room_name}</dd>
      {info.room_location ? (
        <>
          <dt className="font-semibold text-ink">Adresse</dt>
          <dd className="text-ink-soft">{info.room_location}</dd>
        </>
      ) : null}
      <dt className="font-semibold text-ink">Termin</dt>
      <dd className="text-ink-soft">
        {formatRange(info.starts_at, info.ends_at)} Uhr
      </dd>
      <dt className="font-semibold text-ink">Betrag</dt>
      <dd className="text-ink-soft">{formatPriceEur(info.price)}</dd>
      {info.paid_at ? (
        <>
          <dt className="font-semibold text-ink">Bezahlt</dt>
          <dd className="text-ink-soft">
            {formatDate(info.paid_at, "dd.MM.yyyy, HH:mm")} Uhr
            {info.payment_method === "manuell" ? " (bei Tanzraum)" : ""}
          </dd>
        </>
      ) : null}
    </dl>
  );
}

function CalendarButtons({
  info,
  token,
}: {
  info: PaymentInfo;
  token: string;
}) {
  return (
    <div className="mt-8 flex flex-wrap justify-center gap-3">
      <a href={bookingsApi.paymentIcsUrl(token)} className={PRIMARY_LINK}>
        <CalendarPlus className="h-5 w-5" aria-hidden="true" />
        In Kalender eintragen
      </a>
      <a
        href={googleCalendarUrl(info)}
        target="_blank"
        rel="noopener noreferrer"
        className={SECONDARY_LINK}
      >
        <CalendarPlus className="h-5 w-5" aria-hidden="true" />
        Google Kalender
      </a>
    </div>
  );
}

function NextSteps({ info }: { info: PaymentInfo }) {
  const steps: { icon: LucideIcon; title: string; text: string }[] = [
    {
      icon: Mail,
      title: "Bestätigung per Email",
      text: "Die Buchungsbestätigung mit Kalender-Eintrag kommt zusätzlich per Email. Schau bitte auch im Spam-Ordner nach.",
    },
  ];
  if (info.payment_method === "stripe") {
    steps.push({
      icon: CreditCard,
      title: "Rechnung",
      text: "Die Rechnung kommt mit separater Email von unserem Zahlungsdienst Stripe.",
    });
  }
  steps.push(
    {
      icon: CalendarCheck,
      title: "Am Termin",
      text: info.room_location
        ? `Komm einfach zur gebuchten Zeit in ${info.room_name}, ${info.room_location}.`
        : `Komm einfach zur gebuchten Zeit in ${info.room_name}.`,
    },
    {
      icon: XCircle,
      title: "Doch keine Zeit?",
      text: "Storniere bitte rechtzeitig über den Link in der Bestätigungs-Email, dann kann jemand anderes den Raum nutzen.",
    },
  );

  return (
    <section className="mt-10 text-left">
      <h2 className="mb-4 font-display text-h4 uppercase text-primary">
        So geht es weiter
      </h2>
      <ol className="space-y-4">
        {steps.map(({ icon: Icon, title, text }, idx) => (
          <li key={title} className="flex gap-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-body-sm font-semibold text-white">
              {idx + 1}
            </span>
            <div>
              <p className="flex items-center gap-2 font-semibold text-ink">
                <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
                {title}
              </p>
              <p className="text-body-sm text-ink-soft">{text}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function PaymentStatusPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const ergebnis = params.get("ergebnis");
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  const query = useQuery<PaymentInfo, ApiError>({
    queryKey: ["payment-info", token],
    queryFn: () => bookingsApi.paymentInfo(token as string),
    enabled: Boolean(token) && ergebnis !== "ungueltig",
    retry: (count, err) => err.status !== 404 && count < 2,
    // Nach der Rückkehr aus Stripe abfragen, bis der Webhook bestätigt hat.
    refetchInterval: (q) =>
      ergebnis === "danke" &&
      q.state.data?.status === "awaiting_payment" &&
      !waitedTooLong
        ? CONFIRM_POLL_MS
        : false,
  });

  useEffect(() => {
    if (ergebnis !== "danke") return;
    const timer = window.setTimeout(
      () => setWaitedTooLong(true),
      CONFIRM_WAIT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [ergebnis]);

  const kind = resolvePaymentView({
    token,
    ergebnis,
    info: query.data,
    error: query.error,
    waitedTooLong,
  });
  const info = query.data;

  if (kind === "loading") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <p className="text-body-sm text-ink-muted">
          Zahlungsstand wird geladen …
        </p>
      </div>
    );
  }

  const view = VIEWS[kind];
  const Icon = view.icon;
  const showPay =
    info?.can_pay &&
    info.pay_url &&
    ["open", "aborted", "stripe_error"].includes(kind);

  return (
    <div className="mx-auto max-w-2xl px-6 py-16 text-center">
      <Icon
        className={`mx-auto mb-4 h-14 w-14 ${
          view.tone === "success"
            ? "text-success"
            : view.tone === "danger"
              ? "text-danger"
              : "text-primary"
        } ${kind === "waiting" ? "animate-spin" : ""}`}
        aria-hidden="true"
      />
      <h1 className="mb-4 font-display text-h1 uppercase text-primary">
        {view.title}
      </h1>

      <div
        role={kind === "waiting" ? "status" : undefined}
        aria-live={kind === "waiting" ? "polite" : undefined}
        className={`mb-8 rounded-card border p-4 text-lead ${TONE_CLASSES[view.tone]}`}
      >
        {view.text(info)}
      </div>

      {info && kind !== "invalid" && kind !== "network_error" ? (
        <BookingSummary info={info} />
      ) : null}

      {showPay && info?.pay_url ? (
        <div className="mt-8">
          <a href={info.pay_url} className={PRIMARY_LINK}>
            <CreditCard className="h-5 w-5" aria-hidden="true" />
            {kind === "open" ? "Jetzt bezahlen" : "Erneut bezahlen"}
          </a>
        </div>
      ) : null}

      {kind === "confirmed" && info && token ? (
        <CalendarButtons info={info} token={token} />
      ) : null}
      {kind === "confirmed" && info ? <NextSteps info={info} /> : null}

      {kind === "network_error" ? (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className={`mt-2 ${PRIMARY_LINK}`}
        >
          Seite neu laden
        </button>
      ) : null}

      <div className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 text-body-sm">
        <Link to="/konto/buchungen" className="text-primary underline">
          Meine Buchungen
        </Link>
        {kind === "expired" || kind === "cancelled" || kind === "rejected" ? (
          <Link to="/kalender" className="text-primary underline">
            Freie Zeiten ansehen
          </Link>
        ) : null}
        <a
          href="mailto:info@tanzschule-tanzraum.de"
          className="text-primary underline"
        >
          Fragen? info@tanzschule-tanzraum.de
        </a>
      </div>
    </div>
  );
}
